import { useEffect, useRef } from 'react';
import { Search as SearchIcon } from '@mui/icons-material';
import { Paper, Table, TableBody, TableRow } from '@mui/material';
import { CustomPrimaryUser } from 'api';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import UserRow from '../UserRow';
import { UsersTableProps } from './UsersTable.props';
import {
  StyledAutocomplete,
  StyledAutocompleteInput,
  StyledFilterList,
  StyledRoot,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHead,
} from './UsersTable.styles';

const UsersTable = ({
  firm,
  setCurrentUser,
  isSuperUser,
  isFirmAdmin,
  users,
  isLoading,
  userSearchValue,
  setUserSearchValue,
  togglePrimaryUser,
  is409a,
  currentUser,
}: UsersTableProps) => {
  const tableRef = useRef<HTMLDivElement>(null);

  const handleSearchChange = (event: unknown, value: unknown) => {
    if (value && typeof value === 'string') {
      setUserSearchValue(value);
    } else if (value && typeof value === 'object' && value !== null) {
      setUserSearchValue((value as CustomPrimaryUser)?.email ?? '');
    } else {
      setUserSearchValue('');
    }
  };

  useEffect(() => {
    const tableContainer = tableRef.current;
    if (tableContainer) {
      tableContainer.scrollTop = 0; // Reset scroll position when users change
    }
  }, [users]);

  if (isLoading) {
    return <LoadingIndicator isLoading />;
  }

  return (
    <StyledRoot>
      <StyledAutocomplete
        id="user-search"
        options={users ?? []}
        getOptionLabel={user => (user as CustomPrimaryUser).email ?? ''}
        style={{ width: 300, marginTop: '1.75rem' }}
        value={userSearchValue}
        onChange={handleSearchChange}
        renderInput={params => (
          <StyledAutocompleteInput
            {...params}
            variant="outlined"
            margin="dense"
            label="Filter users by email"
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: <SearchIcon color="action" />,
                endAdornment: (
                  <>
                    {userSearchValue === '' || userSearchValue === null ? (
                      <StyledFilterList color="action" />
                    ) : (
                      params.InputProps.endAdornment
                    )}
                  </>
                ),
              },
            }}
            fullWidth
          />
        )}
        freeSolo
      />
      <StyledTableContainer ref={tableRef}>
        <Table>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Last Login</StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {users?.map(user => (
              <UserRow
                key={user.id}
                user={user}
                firm={firm}
                setCurrentUser={setCurrentUser}
                isSuperUser={isSuperUser}
                isFirmAdmin={isFirmAdmin}
                togglePrimaryUser={togglePrimaryUser}
                is409a={is409a}
                currentUser={currentUser}
              />
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </StyledRoot>
  );
};

export default UsersTable;
